import { fromPairs, groupBy, mapValues, sumBy } from 'lodash';
import { arrayOf, func, object, shape, string } from 'prop-types';
import React from 'react';
import DoubleScrollBar from 'react-double-scrollbar';
import { connect } from 'react-redux';
import { Col, Collapse, Label, Row } from 'reactstrap';

import Association from '../../associations/Association';
import { getProductLabel } from '../../campaign-editor/transform-for-rendering';
import { commercialIncentivesOptions } from '../../constants';
import SingleSelect from '../../generic/SingleSelect';
import { getTooltipLabelWithText } from '../../generic/Tooltip';
import { campaignShape, productShape } from '../../prop-types';
import api from '../../REST/api';
import { productAggregatesMapShape } from '../change-product-budget/constants';
import { getProductRowId } from '../change-product-budget/utils';
import HELP_TEXTS from '../HELP_TEXTS';
import { BUDGET_FIELDS, BUDGET_FIELDS_VALUES } from './constants';
import TransferBudgetTable from './TransferBudgetTable';
import { calculateTotalDelta } from './utils';

export function TransferBudgetRequestForm({
  value,
  onChange,
  campaign,
  products,
  targetCampaign,
  setTargetCampaign,
  targetProductAggregates,
  targetProductsAggregatesOnChange,
  setTargetProductAggregates,
  targetCommercialIncentive,
  setTargetCommercialIncentive,
}) {
  const { productAggregates } = value;
  const sourceProdAggr = Object.values(productAggregates);
  const targetProdAggr =
    targetProductAggregates &&
    Object.values(targetProductAggregates.productAggregates);
  const allBudgets = Object.values(BUDGET_FIELDS);

  const sourceDeltas = Object.fromEntries(
    allBudgets.map(field => [
      field,
      calculateTotalDelta(sourceProdAggr, field),
    ]),
  );

  const targetDeltas =
    targetProductAggregates &&
    Object.fromEntries(
      allBudgets.map(field => [
        field,
        calculateTotalDelta(targetProdAggr, field),
      ]),
    );

  const campaignSelectionOnChange = e => {
    api.getCampaign({ id: e.target.value }).then(selectedCampaign => {
      const targetCL = selectedCampaign?.campaignLines.map(campaignLine => {
        const productCL = products.find(
          product => product.id === campaignLine.product.id,
        );
        return {
          ...campaignLine,
          adjustedFee: campaignLine.adjustedFee * 100,
          product: productCL,
        };
      });

      const completeTargetCampaign = {
        ...selectedCampaign,
        campaignLines: targetCL,
      };

      const campaignLines = Object.values(completeTargetCampaign.campaignLines);
      const campaignLinesByProductRowId = groupBy(
        campaignLines,
        getProductRowId,
      );
      const targetProductsAggregate = mapValues(
        campaignLinesByProductRowId,
        lines => {
          const rep = lines[0]; // representative line
          const { product } = rep;
          const values = fromPairs(
            BUDGET_FIELDS_VALUES.map(budgetField => [
              budgetField,
              sumBy(lines, budgetField),
            ]),
          );
          return {
            label: getProductLabel(product),
            product,
            adjustedFee: rep.adjustedFee / 100 || 0,
            lineIds: lines.map(({ id }) => id),
            oldValues: values,
            newValues: values,
          };
        },
      );
      setTargetCampaign(completeTargetCampaign);
      setTargetProductAggregates({ ...targetProductsAggregate });
    });
  };

  const targetCampaignSearchParams = {
    accountSfId: campaign.opportunity.accountSfId,
    isEligibleForChangeRequest: true,
  };

  const targetNewFreeService = sumBy(targetProdAggr, 'newValues.freeService');
  const targetOldFreeService = sumBy(targetProdAggr, 'oldValues.freeService');
  const hasCommercialIncentive =
    targetCampaign && targetCampaign.commercialIncentive;
  const showCommercialIncentive =
    targetOldFreeService === 0 &&
    targetNewFreeService > 0 &&
    !hasCommercialIncentive;
  const TooltipLabel = getTooltipLabelWithText(HELP_TEXTS);
  return (
    <>
      <Row className="mt-5">
        <Col sm={5}>
          <h6>
            <TooltipLabel
              helpKey="account"
              text="Account"
              section="transferBudget"
              colWidth={2.5}
              resource="tr"
            />{' '}
            : <span>{campaign.opportunity.accountSfId}</span>
          </h6>
        </Col>
      </Row>
      <Row className="mt-5 mb-5">
        <Col sm={5}>
          <Label for="target-campaign-selection">
            Transfer budget from {campaign.nCode} - {campaign.name} to:
          </Label>
        </Col>
        <Col sm={7}>
          <Association
            onChange={e => campaignSelectionOnChange(e)}
            name="targetCampaignId"
            type="campaign"
            id="target-campaign-selection"
            value={targetCampaign?.id}
            searchParams={targetCampaignSearchParams}
            removeOption={campaign.id}
          />
        </Col>
      </Row>
      <DoubleScrollBar>
        <Label for="source-campaign-table" className="mb-0 mt-3">
          Source Campaign: {campaign.nCode} - {campaign.name}
        </Label>
        <TransferBudgetTable
          sourceCampaign
          id="source-campaign-table"
          productAggregatesMap={productAggregates}
          targetCampaign={targetCampaign}
          targetProductAggregatesMap={
            targetProductAggregates && targetProductAggregates.productAggregates
          }
          onChange={onChange}
          campaign={campaign}
          otherDeltas={targetDeltas}
        />
        {targetProductAggregates && (
          <>
            <Label for="target-campaign-table">
              Target Campaign: {targetCampaign.nCode} - {targetCampaign.name}
            </Label>
            <TransferBudgetTable
              id="target-campaign-table"
              productAggregatesMap={targetProductAggregates.productAggregates}
              onChange={targetProductsAggregatesOnChange}
              campaign={targetCampaign}
              otherDeltas={sourceDeltas}
            />
          </>
        )}
      </DoubleScrollBar>
      <Collapse
        name="commercialIncentive"
        isOpen={showCommercialIncentive}
        className="mt-4"
      >
        <Row>
          <Label sm={2} for="ca-commercialIncentive">
            Commercial Incentive
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={commercialIncentivesOptions}
              id="ca-commercialIncentive"
              name="commercialIncentive"
              value={targetCommercialIncentive}
              onChange={e => setTargetCommercialIncentive(e)}
            />
          </Col>
        </Row>
      </Collapse>
    </>
  );
}

TransferBudgetRequestForm.propTypes = {
  onChange: func.isRequired,
  campaign: campaignShape.isRequired,
  value: shape({
    commercialIncentive: string,
    productAggregates: productAggregatesMapShape.isRequired,
  }).isRequired,
  products: arrayOf(productShape).isRequired,
  targetProductsAggregatesOnChange: func.isRequired,
  targetProductAggregates: object, // eslint-disable-line react/forbid-prop-types
  setTargetProductAggregates: func.isRequired,
  targetCampaign: campaignShape,
  setTargetCampaign: func.isRequired,
  targetCommercialIncentive: string,
  setTargetCommercialIncentive: func.isRequired,
};

TransferBudgetRequestForm.defaultProps = {
  targetProductAggregates: undefined,
  targetCampaign: undefined,
  targetCommercialIncentive: undefined,
};

const mapStateToProps = state => ({ products: state.products });
const TransferBudgetForm = connect(mapStateToProps)(TransferBudgetRequestForm);
export default TransferBudgetForm;
